import { ApiKeyStore, looksLikeApiKey } from '@/capture/ApiKeyStore';
import { formatBytes, type TagImage } from '@/semantics/TagImage';
import { SitePlanError } from '@/reference/SitePlanClient';
import {
  IMAGE_ACCEPT_ATTRIBUTE,
  imagesFromClipboard,
  imagesFromDrop,
  intakeImages,
  pasteCarriesText,
  tagImageToBlob,
} from './ImageIntake';
import type { CompileWarning } from '@/reference/SitePipelineCompiler';
import {
  DEFAULT_WAND_MODEL,
  WAND_MODELS,
  WAND_REFINEMENTS,
  buildWandPrompt,
  castWandRequest,
  describeWandReport,
  starterPrompts,
  targetHeadline,
  targetPlaceholder,
  type WandContext,
  type WandReport,
  type WandSpell,
  type WandStarter,
  wandReportIsEmpty,
  type WandTurn,
} from '@/wand';
import type { FloraCatalog } from '@/flora/FloraCatalog';
import { append, clear, el, formatArea, setClass } from './dom';
import { icon } from './Icons';
import type { DisplayUnit } from './UiPreferences';

/**
 * The wand's prompt bubble: a floating panel anchored to the place that was aimed at.
 *
 * ## Why a bubble and not a dock panel
 *
 * The request is *about a place*. A panel pinned to the right-hand rail would put the sentence and
 * the thing it describes at opposite ends of the screen, and every request would begin with the user
 * re-establishing which spot they meant. The bubble opens where the gesture was and the tool draws a
 * ring — or the outline they traced — on the ground beneath it, so the two halves of "here, do this"
 * stay together.
 *
 * ## The context chips are the trust surface
 *
 * The row under the header lists exactly what the model will be told, one chip per fact, each
 * removable. Nothing is sent that is not a chip. This is the answer to the only question that matters
 * before someone types into a box that will edit their work — *what does it know about my site?* —
 * and it is answerable by looking rather than by trusting, because the chips are rendered from the
 * same {@link WandContext} the prompt serializes.
 *
 * ## A log and a composer, not a sequence of screens
 *
 * This panel used to be a state machine that swapped its whole body: ask, then working, then result,
 * then — for a follow-up question — ask again with the box still holding the sentence that had just
 * been sent. Three things were wrong with that, and they were the same thing three times: the panel
 * kept **replacing** what the designer was reading with what it wanted them to read next.
 *
 * - Answering a question meant editing the previous request in place, because it was still sitting
 *   in the box. The wand asked "how wide?" and the only way to answer was to rewrite the sentence
 *   that prompted the question.
 * - Getting to a result destroyed the request that produced it, so "the summary says 19 plants and
 *   the ledger says nothing changed" could not be read next to what had been asked for.
 * - Every turn after the first was reachable only through a `Refine…` button — a mode, for the thing
 *   the feature exists to do.
 *
 * So the body is now a **transcript with a composer under it**. The box is always there and always
 * empty: what was said moves up into the log the instant it is sent, which is the only arrangement
 * in which "always clear" and "nothing is lost" are both true. Turns accumulate, each one compressed
 * to its summary and its ledger with the detail behind a disclosure, so a four-turn conversation
 * about one corner still fits in the panel.
 *
 * ## Applied first, confirmed after
 *
 * A result is applied the moment it arrives, as one undo step, and its log entry carries the Undo.
 * The alternative — a preview to approve — would ask the user to evaluate a description of a change
 * instead of the change, in a 3D tool where looking at it is the whole point. Undo is already the
 * app's answer to a bad edit; this feature does not need a second one.
 */

export interface WandBubbleDeps {
  /** The HUD root, so the bubble inherits the theme and disappears in photo mode. */
  host: HTMLElement;
  catalog: FloraCatalog;
  keys: ApiKeyStore;
  /**
   * Apply a spell to the live site. Returns the compiler's account of what happened.
   *
   * The attachments travel with the spell because a `tag` op may claim one — that is how a
   * photograph pasted into a request stops being evidence for one turn and becomes part of the
   * document, reaching every future render.
   */
  apply: (spell: WandSpell, attachments: readonly TagImage[]) => WandReport;
  /** Take the last applied spell back off the history. */
  undo: () => void;
  /** Called when the bubble closes, however it was dismissed. */
  onClose?: () => void;
  /** Called whenever a request starts or finishes, so the ring can pulse. */
  onBusyChange?: (busy: boolean) => void;
  displayUnit?: () => DisplayUnit;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

/**
 * One line of the conversation.
 *
 * A request and its answer are separate entries rather than one paired record, because they do not
 * always pair: a request can be answered with questions, or with an error, and a retry answers a
 * request that already has an entry. Keeping them flat means the log never has to decide which of
 * several answers belongs to which half of a pair.
 */
type LogEntry =
  | { kind: 'request'; text: string; images: number }
  | { kind: 'result'; spell: WandSpell; report: WandReport; undone: boolean }
  | { kind: 'questions'; questions: readonly string[] }
  | { kind: 'error'; message: string };

const MODEL_STORAGE_KEY = 'landscape-glade.wand-model';

/**
 * Pictures one request may carry — the API client's own ceiling, enforced here so the limit is
 * learned before waiting for a call rather than after.
 */
const MAX_WAND_IMAGES = 6;

/** Phase copy. Kept together so the wand's voice can be read in one place rather than hunted for. */
const WORKING_STEPS = ['Reading the site…', 'Consulting…', 'Casting…'] as const;

export class WandBubble {
  readonly root: HTMLElement;

  private readonly body: HTMLElement;
  private readonly headline: HTMLElement;
  private readonly input: HTMLTextAreaElement;
  private readonly fileInput: HTMLInputElement;

  private context: WandContext | null = null;
  private entries: LogEntry[] = [];
  /** The request currently in flight. Rendered as the last log entry, with a live status line. */
  private pending: string | null = null;
  private thread: WandTurn[] = [];
  /**
   * Attached photographs, already downscaled and encoded.
   *
   * Held as stored images rather than as the original `File`s so that the bytes sent to the model
   * and the bytes a `tag` op may keep are the same bytes. Encoding twice would mean the picture in
   * the document is not quite the picture the wand was looking at when it described it.
   */
  private attachments: TagImage[] = [];
  private intakeError = '';
  private droppedContext = new Set<string>();
  private model: string = readModel();
  private needsKey = false;

  private controller: AbortController | null = null;
  private stepTimer: ReturnType<typeof setInterval> | null = null;
  private step = 0;
  /** The live status line of the pending entry, updated in place so a render cannot steal focus. */
  private stepLabel: HTMLElement | null = null;
  private transcript: HTMLElement | null = null;
  private keyDraft = '';
  private keyError = '';

  constructor(private readonly deps: WandBubbleDeps) {
    this.headline = el('span', { class: 'lg-wand__headline' });
    this.body = el('div', { class: 'lg-wand__body' });

    this.input = el('textarea', {
      class: 'lg-wand__input',
      attrs: { rows: '2', 'aria-label': 'Your request for the wand', spellcheck: 'true' },
      on: {
        keydown: (event) => this.handleInputKey(event),
        input: () => this.autoGrow(),
      },
    });

    this.fileInput = el('input', {
      class: 'lg-wand__file',
      attrs: {
        type: 'file',
        accept: IMAGE_ACCEPT_ATTRIBUTE,
        multiple: true,
        'aria-label': 'Attach evidence photographs',
      },
      on: { change: () => void this.adoptFiles() },
    });

    this.root = el(
      'div',
      { class: 'lg-wand', attrs: { role: 'dialog', 'aria-label': 'Magic wand' } },
      el(
        'div',
        { class: 'lg-wand__head' },
        el('span', { class: 'lg-wand__mark', html: icon('wand', 16) }),
        el(
          'div',
          { class: 'lg-wand__heading' },
          el('span', { class: 'lg-wand__title', text: 'Magic wand' }),
          this.headline,
        ),
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the wand' },
          on: { click: () => this.close() },
        }),
      ),
      this.body,
      this.fileInput,
    );

    // The editor listens on `window`; a stray `P` typed into the ask box must not arm planting.
    this.root.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });
    this.root.addEventListener('pointerdown', (event) => event.stopPropagation());

    /*
     * Paste anywhere in the bubble.
     *
     * Bound on the whole bubble rather than on the text box because a reference is nearly always
     * something just snipped from a browser or a screenshot tool, and by the time someone has it on
     * the clipboard they are not thinking about which element has focus. Text pastes are left alone
     * — the textarea's own default handles those — so pasting a sentence into the request still
     * types the sentence.
     */
    this.root.addEventListener('paste', (event) => {
      const files = imagesFromClipboard(event);
      if (files.length === 0) return;
      // A paste carrying both an image and text, into the request box, is someone pasting text that
      // happened to be copied alongside a picture. Their words win.
      if (pasteCarriesText(event) && document.activeElement === this.input) return;
      event.preventDefault();
      event.stopPropagation();
      void this.addImages(files);
    });

    // Drop, for the folder case. `dragover` must be cancelled or the browser navigates to the file.
    this.root.addEventListener('dragover', (event) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      event.stopPropagation();
      setClass(this.root, 'is-dropping', true);
    });
    this.root.addEventListener('dragleave', (event) => {
      if (event.target !== this.root) return;
      setClass(this.root, 'is-dropping', false);
    });
    this.root.addEventListener('drop', (event) => {
      setClass(this.root, 'is-dropping', false);
      const files = imagesFromDrop(event);
      if (files.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      void this.addImages(files);
    });
  }

  get isOpen(): boolean {
    return this.root.isConnected;
  }

  private get busy(): boolean {
    return this.pending !== null;
  }

  /** Open against a freshly aimed target. Any previous conversation is dropped — this is a new spot. */
  open(context: WandContext, screen: { x: number; y: number }): void {
    this.cancel();
    this.context = context;
    this.entries = [];
    this.pending = null;
    this.thread = [];
    this.attachments = [];
    this.intakeError = '';
    this.droppedContext.clear();
    this.input.value = '';
    this.needsKey = !this.deps.keys.has();
    if (!this.root.isConnected) this.deps.host.appendChild(this.root);
    this.position(screen);
    this.render();
    this.input.focus();
  }

  close(): void {
    this.cancel();
    this.root.remove();
    this.context = null;
    this.deps.onClose?.();
  }

  /** Abort an in-flight request without closing. */
  cancel(): void {
    this.controller?.abort();
    this.controller = null;
    this.pending = null;
    this.stopSteps();
    this.deps.onBusyChange?.(false);
  }

  dispose(): void {
    this.cancel();
    this.root.remove();
  }

  /* --------------------------------- Placement --------------------------------- */

  /**
   * Sit the bubble beside the gesture, then pull it back inside the viewport.
   *
   * Offset rather than centred, so the bubble never covers the ground it is a request about — the
   * user has to be able to see the spot while describing it.
   */
  private position(screen: { x: number; y: number }): void {
    const width = 340;
    const margin = 12;
    const left = Math.min(
      Math.max(margin, screen.x + 20),
      Math.max(margin, window.innerWidth - width - margin),
    );
    const top = Math.min(
      Math.max(64, screen.y - 40),
      Math.max(64, window.innerHeight - 260),
    );
    this.root.style.left = `${left}px`;
    this.root.style.top = `${top}px`;
  }

  /* ---------------------------------- Render ---------------------------------- */

  /**
   * Rebuild the body.
   *
   * The textarea is a long-lived element moved between renders rather than recreated, so a
   * half-typed sentence survives toggling a context chip. Focus is restored explicitly because
   * removing the focused node from the document drops it, and the composer is now on screen the
   * whole time — losing the caret mid-sentence would be constant rather than occasional.
   */
  private render(): void {
    const context = this.context;
    if (!context) return;

    const unit = this.deps.displayUnit?.() ?? 'meters';
    const hadFocus = document.activeElement === this.input;
    const caret = this.input.selectionStart;

    this.headline.textContent = targetHeadline(context, unit);
    setClass(this.root, 'is-working', this.busy);
    clear(this.body);
    this.stepLabel = null;
    this.transcript = null;

    if (this.needsKey) {
      this.renderKeyPrompt();
      return;
    }

    append(this.body, this.contextChips());
    if (this.entries.length > 0 || this.pending !== null) {
      this.transcript = this.renderTranscript();
      append(this.body, this.transcript);
    }
    append(this.body, this.renderComposer());
    this.autoGrow();
    this.scrollTranscript();

    if (hadFocus) {
      this.input.focus();
      this.input.setSelectionRange(caret, caret);
    }
  }

  /** The first-run key row. Inline rather than a second dialog — one interruption, not two. */
  private renderKeyPrompt(): void {
    const field = el('input', {
      class: 'lg-input',
      attrs: { type: 'password', placeholder: 'sk-…', 'aria-label': 'OpenAI API key', spellcheck: 'false' },
      on: {
        input: (event) => {
          this.keyDraft = (event.target as HTMLInputElement).value;
        },
        keydown: (event) => {
          if (event.key === 'Enter') this.saveKey();
        },
      },
    });

    append(
      this.body,
      el('p', {
        class: 'lg-wand__note',
        text: 'The wand needs an OpenAI key. It is stored in this browser only and goes straight to OpenAI — the same key photo enhancement uses.',
      }),
      this.keyError.length > 0 ? el('p', { class: 'lg-wand__error', text: this.keyError }) : null,
      el('div', { class: 'lg-wand__keyrow' }, field, this.button('Save key', () => this.saveKey(), true)),
    );
    field.focus();
  }

  private saveKey(): void {
    if (!looksLikeApiKey(this.keyDraft)) {
      this.keyError = 'That does not look like an OpenAI key — they begin with “sk-”.';
      this.render();
      return;
    }
    this.deps.keys.set(this.keyDraft);
    this.keyDraft = '';
    this.keyError = '';
    this.needsKey = false;
    this.render();
    this.input.focus();
  }

  /**
   * The context row.
   *
   * Labelled "The wand will know", present tense, because it is a statement about the request that is
   * about to be sent rather than a summary of one that was.
   */
  private contextChips(): HTMLElement {
    const context = this.context;
    const row = el('div', { class: 'lg-wand__context' });
    if (!context) return row;

    const unit = this.deps.displayUnit?.() ?? 'meters';
    const region = context.target.region;
    const chips: { id: string; label: string; title: string; fixed?: boolean }[] = [
      {
        id: 'site',
        label: `Site · ${context.site.plantCount} plants`,
        title: `${context.site.widthMeters} × ${context.site.lengthMeters} m, ${context.site.speciesCount} species. Always sent — without it the wand cannot place anything on your ground.`,
        fixed: true,
      },
      // One chip, two readings, because there is one gesture with two outcomes: the point you
      // clicked, or the shape you drew. Removing it makes the request about the whole site either
      // way, which is the only thing "leave it out" can honestly mean.
      region
        ? {
            id: 'point',
            label: `This area · ${formatArea(region.areaMeters, unit)}`,
            title:
              `The outline you drew: ${region.polygon.length} points, ` +
              `${formatArea(region.areaMeters, unit)}, bounding box ${region.widthMeters} × ${region.lengthMeters} m. ` +
              'Sent verbatim, so the wand can shape the change to it. Remove to make the request about the whole site.',
          }
        : {
            id: 'point',
            label: 'This spot',
            title: `The point you clicked: ${context.target.point.x} , ${context.target.point.z}. Remove to make the request about the whole site.`,
          },
    ];

    // Only present while the grid overlay is on, which is also the only time it is sent — the chip
    // and the overlay are two views of the same switch.
    if (context.grid) {
      chips.push({
        id: 'grid',
        label: context.target.gridCell ? `Grid · ${context.target.gridCell}` : 'Grid',
        title:
          `The reference grid you have switched on: ${context.grid.spacingMeters} m cells, ` +
          `A1 at the north-west corner. Lets you name places by cell — “the bed in C4”.`,
      });
    }
    if (context.target.plant) {
      chips.push({
        id: 'target',
        label: context.target.plant.name,
        title: `The plant you clicked (${context.target.plant.id}).`,
      });
    }
    if (context.target.structure) {
      chips.push({ id: 'target', label: context.target.structure.name, title: 'The structure you clicked.' });
    }
    if (context.selection.length > 0) {
      chips.push({
        id: 'selection',
        label: `${context.selection.length} selected`,
        title: 'Your current selection, which the wand can act on as a group.',
      });
    }
    if (context.nearby.length > 0) {
      chips.push({
        id: 'nearby',
        label: `${context.nearby.length} nearby`,
        title: `Plants close to the target, listed by name and id so the wand can change them individually.`,
      });
    }
    chips.push({
      id: 'environment',
      label: `Day ${context.environment.date.dayOfYear} · ${pad(context.environment.time.hour)}:${pad(context.environment.time.minute)}`,
      title: 'The current date, time and weather.',
    });

    append(row, el('span', { class: 'lg-wand__contextlabel', text: 'The wand will know' }));
    for (const chip of chips) {
      const dropped = this.droppedContext.has(chip.id);
      row.appendChild(
        el('button', {
          class: `lg-wand__chip${dropped ? ' is-dropped' : ''}${chip.fixed ? ' is-fixed' : ''}`,
          text: chip.label,
          title: chip.fixed ? chip.title : `${chip.title}\n\nClick to leave it out.`,
          attrs: { type: 'button', 'aria-pressed': !dropped, disabled: chip.fixed === true },
          on: {
            click: () => {
              if (chip.fixed) return;
              if (dropped) this.droppedContext.delete(chip.id);
              else this.droppedContext.add(chip.id);
              this.render();
            },
          },
        }),
      );
    }

    // Numbered, because the number is load-bearing: the prompt tells the model these are "image 1,
    // image 2…", and a `tag` op claims one by that index. A chip that did not show its number would
    // make a spell's `attachImageIndex` unverifiable by looking.
    for (const [index, image] of this.attachments.entries()) {
      row.appendChild(
        el(
          'button',
          {
            class: 'lg-wand__chip lg-wand__chip--photo',
            title:
              `Image ${index + 1}: ${image.name ?? 'pasted picture'} — ` +
              `${image.widthPx}×${image.heightPx}, ${formatBytes(image.bytes)}.\n` +
              'The wand can keep this on a tag, where it reaches every future render.\n\nClick to remove.',
            attrs: { type: 'button' },
            on: {
              click: () => {
                this.attachments.splice(index, 1);
                this.render();
              },
            },
          },
          el('img', { class: 'lg-wand__thumb', attrs: { src: image.dataUrl, alt: '' } }),
          el('span', { text: `${index + 1}. ${shorten(image.name ?? 'pasted')}` }),
        ),
      );
    }

    return row;
  }

  /* -------------------------------- The transcript -------------------------------- */

  private renderTranscript(): HTMLElement {
    const log = el('div', {
      class: 'lg-wand__log',
      attrs: { role: 'log', 'aria-label': 'What the wand has done here', 'aria-live': 'polite' },
    });

    // Only the newest applied result carries an Undo, because the history is a stack: an Undo on an
    // older entry would take back whatever happened last, which is not what the button says.
    let undoable = -1;
    for (const [index, entry] of this.entries.entries()) {
      if (entry.kind === 'result' && !entry.undone) undoable = index;
    }

    for (const [index, entry] of this.entries.entries()) {
      log.appendChild(this.renderEntry(entry, index === undoable));
    }
    if (this.pending !== null) log.appendChild(this.renderPending(this.pending));
    return log;
  }

  private renderEntry(entry: LogEntry, undoable: boolean): HTMLElement {
    switch (entry.kind) {
      case 'request':
        return el(
          'div',
          { class: 'lg-wand__said' },
          el('span', { class: 'lg-wand__saidmark', text: '›' }),
          el('span', { class: 'lg-wand__saidtext', text: entry.text }),
          entry.images > 0
            ? el('span', {
                class: 'lg-wand__saidmeta',
                text: `+${entry.images} photo${entry.images === 1 ? '' : 's'}`,
              })
            : null,
        );

      case 'result':
        return this.renderResult(entry, undoable);

      case 'questions':
        return el(
          'div',
          { class: 'lg-wand__turn lg-wand__turn--asked' },
          el('p', { class: 'lg-wand__asklabel', text: 'The wand needs to know' }),
          el(
            'ul',
            { class: 'lg-wand__questions' },
            ...entry.questions.map((question) => el('li', { text: question })),
          ),
        );

      case 'error':
        return el(
          'div',
          { class: 'lg-wand__turn lg-wand__turn--failed' },
          el('p', { class: 'lg-wand__error', text: entry.message }),
        );
    }
  }

  /**
   * One applied turn: the wand's sentence, the compiler's count, and the detail folded away.
   *
   * The summary and the ledger stay side by side and unabbreviated, because the two disagreeing is
   * the single most useful thing this panel can show — "planted 19 shrubs" over "nothing changed"
   * is a bug report the designer can read at a glance. Everything else is behind a disclosure: it
   * is worth having, and it is not worth pushing four earlier turns off the top of the log.
   */
  private renderResult(
    entry: Extract<LogEntry, { kind: 'result' }>,
    undoable: boolean,
  ): HTMLElement {
    const ledger = describeWandReport(entry.report);
    const inert = wandReportIsEmpty(entry.report);
    const detail = [
      ...(entry.spell.notes.length > 0 ? [entry.spell.notes] : []),
      ...entry.report.warnings.map((warning: CompileWarning) => warning.message),
      ...entry.report.inertOps.map((kind) => `The wand's “${kind}” step produced nothing on this site.`),
    ];

    return el(
      'div',
      { class: `lg-wand__turn${entry.undone ? ' is-undone' : ''}` },
      el('p', { class: 'lg-wand__summary', text: entry.spell.summary }),
      el('p', {
        class: `lg-wand__ledger${inert ? ' is-inert' : ''}`,
        text: entry.undone ? 'Taken back' : inert ? 'Nothing on the site changed' : ledger,
      }),
      detail.length > 0
        ? el(
            'details',
            { class: 'lg-wand__detail' },
            el('summary', {
              text: `${detail.length} note${detail.length === 1 ? '' : 's'} from the compiler`,
            }),
            el('ul', { class: 'lg-wand__warnings' }, ...detail.map((message) => el('li', { text: message }))),
          )
        : null,
      undoable
        ? el(
            'div',
            { class: 'lg-wand__turnfoot' },
            this.button('Undo', () => {
              this.deps.undo();
              entry.undone = true;
              this.thread.pop();
              this.render();
              this.input.focus();
            }),
          )
        : null,
    );
  }

  private renderPending(request: string): HTMLElement {
    this.stepLabel = el('span', { class: 'lg-wand__step', text: WORKING_STEPS[this.step] ?? WORKING_STEPS[0] });
    return el(
      'div',
      { class: 'lg-wand__turn lg-wand__turn--pending' },
      el(
        'div',
        { class: 'lg-wand__working' },
        el('span', { class: 'lg-wand__spark', html: icon('wand', 18) }),
        this.stepLabel,
      ),
      el('p', { class: 'lg-wand__echo', text: `“${request}”` }),
    );
  }

  private scrollTranscript(): void {
    const log = this.transcript;
    if (!log) return;
    // After layout, so the height it scrolls to is the height it actually has.
    requestAnimationFrame(() => {
      log.scrollTop = log.scrollHeight;
    });
  }

  /* --------------------------------- The composer --------------------------------- */

  /**
   * The box, and everything attached to it.
   *
   * Always present and always empty. Placeholder and starters change with what just happened —
   * answering a question is a different act from refining a result, and both are different from the
   * first thing you say to a fresh spot — but the control itself never moves, because a composer
   * that appears and disappears is a mode.
   */
  private renderComposer(): HTMLElement {
    const context = this.context;
    if (!context) return el('div');

    const last = this.entries[this.entries.length - 1];
    const answering = last?.kind === 'questions';
    this.input.placeholder = answering
      ? 'Answer, and the wand will try again…'
      : this.entries.length > 0
        ? 'What should be different?'
        : targetPlaceholder(context);
    this.input.disabled = this.busy;

    return el(
      'div',
      { class: 'lg-wand__composer' },
      this.input,
      this.starterRow(this.entries.length > 0 && !answering ? [...WAND_REFINEMENTS] : starterPrompts(context)),
      this.attachmentRow(),
      this.composerFoot(),
    );
  }

  private starterRow(starters: readonly WandStarter[]): HTMLElement {
    const row = el('div', { class: 'lg-wand__starters' });
    for (const starter of starters) {
      row.appendChild(
        el('button', {
          class: 'lg-chip lg-chip--ghost',
          text: starter.label,
          title: starter.prompt,
          attrs: { type: 'button', disabled: this.busy },
          on: {
            click: () => {
              this.input.value = starter.prompt;
              this.autoGrow();
              this.input.focus();
              this.input.setSelectionRange(this.input.value.length, this.input.value.length);
            },
          },
        }),
      );
    }
    return row;
  }

  private attachmentRow(): HTMLElement {
    const row = el(
      'div',
      { class: 'lg-wand__attachrow' },
      el(
        'button',
        {
          class: 'lg-wand__attach',
          title:
            'Attach a photograph — of this part of the real site, or of how you want it to look.\n' +
            'The wand reads it for species, materials, sizes and construction, and can keep it on a\n' +
            'tag so the render follows it every time.\n\nYou can also paste or drop one anywhere here.',
          attrs: { type: 'button' },
          on: { click: () => this.fileInput.click() },
        },
        el('span', { class: 'lg-wand__attachicon', html: icon('gallery', 14) }),
        el('span', {
          text:
            this.attachments.length === 0
              ? 'Add a picture — or paste one'
              : `${this.attachments.length} attached · add another`,
        }),
      ),
    );

    if (this.intakeError) {
      row.appendChild(el('span', { class: 'lg-wand__attacherror', text: this.intakeError }));
    }
    return row;
  }

  private composerFoot(): HTMLElement {
    const select = el('select', {
      class: 'lg-select lg-wand__model',
      attrs: { 'aria-label': 'Wand model', disabled: this.busy },
      on: {
        change: (event) => {
          this.model = (event.target as HTMLSelectElement).value;
          writeModel(this.model);
        },
      },
    });
    for (const option of WAND_MODELS) {
      select.appendChild(
        el('option', {
          text: option.label,
          title: option.note,
          attrs: { value: option.id, selected: option.id === this.model },
        }),
      );
    }

    if (this.busy) {
      const stop = this.button('Stop', () => {
        this.cancel();
        this.render();
        this.input.focus();
      });
      return el('div', { class: 'lg-wand__foot' }, select, el('span', { class: 'lg-wand__spacer' }), stop);
    }

    const cast = this.button('Cast', () => this.requestCast(), true);
    cast.title = 'Send the request  ·  Ctrl+Enter';
    cast.prepend(el('span', { class: 'lg-button__icon', html: icon('wand', 14) }));
    return el('div', { class: 'lg-wand__foot' }, select, el('span', { class: 'lg-wand__spacer' }), cast);
  }

  private button(label: string, onClick: () => void, primary = false): HTMLButtonElement {
    return el(
      'button',
      {
        class: `lg-button${primary ? ' lg-button--primary' : ''}`,
        attrs: { type: 'button' },
        on: { click: onClick },
      },
      el('span', { text: label }),
    );
  }

  /* ----------------------------------- Cast ----------------------------------- */

  private handleInputKey(event: KeyboardEvent): void {
    // Ctrl/Cmd+Enter sends; bare Enter keeps making paragraphs, because a request that needs two
    // sentences should not be truncated by the key that ends the first one.
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      this.requestCast();
    }
  }

  private autoGrow(): void {
    this.input.style.height = 'auto';
    this.input.style.height = `${Math.min(160, Math.max(48, this.input.scrollHeight))}px`;
  }

  /**
   * Fire a request from a DOM handler.
   *
   * `cast` handles its own failures — every path through it ends in a rendered log entry — so the
   * promise is deliberately not awaited here; the UI is driven by the renders it performs, not by
   * its result.
   */
  private requestCast(): void {
    void this.cast();
  }

  private async cast(): Promise<void> {
    const context = this.context;
    const request = this.input.value.trim();
    if (!context || request.length === 0 || this.busy) {
      this.input.focus();
      return;
    }
    const apiKey = this.deps.keys.get();
    if (!apiKey) {
      this.needsKey = true;
      this.render();
      return;
    }

    // Emptied here, before anything can fail, and moved into the log in the same breath. The box is
    // the place the *next* thing is said; leaving the last thing in it is what made answering a
    // follow-up question mean editing the question's cause.
    this.input.value = '';
    this.entries.push({ kind: 'request', text: request, images: this.attachments.length });
    this.pending = request;
    this.step = 0;
    this.render();
    this.startSteps();
    this.deps.onBusyChange?.(true);

    const controller = new AbortController();
    this.controller = controller;

    try {
      const prompt = buildWandPrompt({
        context: this.maskedContext(context),
        request,
        catalog: this.deps.catalog,
        thread: this.thread,
        imageCount: this.attachments.length,
        displayUnit: this.deps.displayUnit?.() ?? 'meters',
      });

      this.setStep(1);

      const response = await castWandRequest({
        apiKey,
        prompt,
        images: this.attachments
          .map((image) => tagImageToBlob(image))
          .filter((blob): blob is Blob => blob !== null),
        model: this.model,
        signal: controller.signal,
        ...(this.deps.fetchImpl ? { fetchImpl: this.deps.fetchImpl } : {}),
      });

      if (controller.signal.aborted) return;
      this.setStep(2);

      const spell = response.spell;

      if (spell.questions.length > 0 && spell.ops.length === 0) {
        this.entries.push({ kind: 'questions', questions: spell.questions });
      } else {
        const report = this.deps.apply(spell, this.attachments);
        this.entries.push({ kind: 'result', spell, report, undone: false });
        this.thread.push({ request, summary: spell.summary, ops: spell.ops });
        // Evidence belongs to the turn that used it; keeping it attached would silently re-send a
        // photograph with every later refinement. A picture the spell claimed for a tag has already
        // been copied into the document by the compiler, so clearing here loses nothing that was
        // meant to last.
        this.attachments = [];
        this.intakeError = '';
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      this.entries.push({
        kind: 'error',
        message:
          error instanceof SitePlanError
            ? error.message
            : error instanceof Error
              ? error.message
              : 'The wand failed for an unknown reason.',
      });
    } finally {
      if (this.controller === controller) this.controller = null;
      if (!controller.signal.aborted) this.pending = null;
      this.stopSteps();
      this.deps.onBusyChange?.(false);
    }
    this.render();
    this.input.focus();
  }

  /**
   * Apply the chips the user switched off.
   *
   * Removing a chip has to actually remove the fact from the prompt, not merely grey out a label —
   * otherwise the row is a claim about what is sent rather than a control over it, which is worse
   * than not offering it.
   */
  private maskedContext(context: WandContext): WandContext {
    if (this.droppedContext.size === 0) return context;
    const masked: WandContext = { ...context };
    if (this.droppedContext.has('point')) {
      masked.target = { ...context.target, kind: 'ground' };
      delete masked.target.plant;
      delete masked.target.structure;
      // The outline goes with the point it stands in for: half of "leave the place out" is not a
      // state the chip offers, and a shape with no claim on the request is a shape sent for nothing.
      delete masked.target.region;
    }
    if (this.droppedContext.has('target')) {
      masked.target = { ...masked.target };
      delete masked.target.plant;
      delete masked.target.structure;
    }
    if (this.droppedContext.has('grid')) {
      delete masked.grid;
      // The cell name goes with the frame that explains it; left behind it would be a label the
      // model has no rule for reading.
      masked.target = { ...masked.target };
      delete masked.target.gridCell;
    }
    if (this.droppedContext.has('selection')) masked.selection = [];
    if (this.droppedContext.has('nearby')) masked.nearby = [];
    return masked;
  }

  /**
   * Advance the status line.
   *
   * Written straight into the node rather than through `render`, because the composer is on screen
   * while a request is in flight and re-rendering it every four seconds would evict the caret from
   * a sentence the designer is already typing.
   */
  private setStep(step: number): void {
    this.step = Math.min(WORKING_STEPS.length - 1, step);
    if (this.stepLabel) this.stepLabel.textContent = WORKING_STEPS[this.step] ?? WORKING_STEPS[0];
  }

  private startSteps(): void {
    this.stopSteps();
    // Advance the label on a timer as well as at real milestones: the API call is the long step, and
    // a label frozen for twenty seconds reads as a hang whether or not it is one.
    this.stepTimer = setInterval(() => {
      if (!this.busy) return;
      this.setStep(this.step + 1);
    }, 4000);
  }

  private stopSteps(): void {
    if (this.stepTimer !== null) clearInterval(this.stepTimer);
    this.stepTimer = null;
    this.stepLabel = null;
  }

  private async adoptFiles(): Promise<void> {
    const files = [...(this.fileInput.files ?? [])];
    this.fileInput.value = '';
    await this.addImages(files);
  }

  /**
   * Take pictures in from any of the three routes.
   *
   * Downscaled and re-encoded here rather than at send time, so the chip can state what the picture
   * actually costs and so a `tag` op keeps exactly what the model saw.
   */
  private async addImages(files: readonly File[]): Promise<void> {
    const room = MAX_WAND_IMAGES - this.attachments.length;
    if (room <= 0) {
      this.intakeError = `The wand takes ${MAX_WAND_IMAGES} pictures at a time.`;
      this.render();
      return;
    }

    const { images, rejected } = await intakeImages(files, { limit: room });
    this.attachments = [...this.attachments, ...images];
    this.intakeError = rejected.length > 0 ? (rejected[0] as string) : '';
    this.render();
  }
}

function readModel(): string {
  try {
    return globalThis.localStorage?.getItem(MODEL_STORAGE_KEY) ?? DEFAULT_WAND_MODEL;
  } catch {
    return DEFAULT_WAND_MODEL;
  }
}

function writeModel(model: string): void {
  try {
    globalThis.localStorage?.setItem(MODEL_STORAGE_KEY, model);
  } catch {
    // Non-fatal: the choice holds for this session only.
  }
}

function shorten(name: string): string {
  return name.length <= 16 ? name : `${name.slice(0, 13)}…`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
